// src/pages/admin/tabs/districts/DistrictEditPage.jsx
import React from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AdminAPI } from '../../../../api/admin';
import { normalizeMediaUrl } from '../../../../api/base';
import { Card, Grid, Row2, Row3, Field, Actions, Btn, Badge, TableWrap, Table, YesNoToggle } from '../../ui';
import MediaPickerModal from '../../components/MediaPickerModal';

const Wrap = styled.div`display:grid; gap:16px;`;

export default function DistrictEditPage(){
  const nav = useNavigate();
  const { id } = useParams(); // 'new' | number | undefined
  const { state } = useLocation();
  const isNew = !id || id === 'new';

  const [regions, setRegions] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [saved, setSaved] = React.useState(false);

  const [form, setForm] = React.useState({
    name_ru:'', name_en:'', name_zh:'', slug:'', description_ru:'', description_en:'', description_zh:'',
    region_id:'', is_active:true, sort_order:0, lat:'', lng:'', address:'',
  });

  const [images, setImages] = React.useState([]);
  const [pickerOpen, setPickerOpen] = React.useState(false);

  const patch = (p)=> setForm(f=>({ ...f, ...p }));

  const loadRegions = React.useCallback(async ()=>{
    try{ const r = await AdminAPI.listRegions({ page_size: 1000 }); setRegions(r.regions || []); }catch(e){ console.error(e); }
  },[]);

  const loadDistrict = React.useCallback(async ()=>{
    if (isNew) return;
    setBusy(true); setErr('');
    try{
      // Списка/детального енда нет — используем list и фильтруем; или state
      if (state?.district) {
        const d = state.district;
        patch({
          name_ru: d.name_ru || '', name_en: d.name_en || '', name_zh: d.name_zh || '', slug: d.slug || '',
          description_ru: d.description_ru || '', description_en: d.description_en || '', description_zh: d.description_zh || '',
          region_id: d.region_id || '', is_active: !!d.is_active, sort_order: d.sort_order || 0,
          lat: d.lat ?? '', lng: d.lng ?? '', address: d.address || '',
        });
      } else {
        const resp = await AdminAPI.listDistricts({ page:1, page_size:1, id });
        const d = (resp.districts || resp.items || []).find(x=>String(x.id)===String(id));
        if (!d) throw new Error('District not found');
        patch({
          name_ru: d.name_ru || '', name_en: d.name_en || '', name_zh: d.name_zh || '', slug: d.slug || '',
          description_ru: d.description_ru || '', description_en: d.description_en || '', description_zh: d.description_zh || '',
          region_id: d.region_id || '', is_active: !!d.is_active, sort_order: d.sort_order || 0,
          lat: d.lat ?? '', lng: d.lng ?? '', address: d.address || '',
        });
      }
    }catch(e){ setErr(e?.message || 'Ошибка загрузки'); }
    finally{ setBusy(false); }
  },[id, isNew, state]);

  const loadImages = React.useCallback(async ()=>{
    if (isNew) return;
    try{
      const res = await AdminAPI.listDistrictMedia(id);
      setImages((res.images || []).map(it=> ({ ...it, url: normalizeMediaUrl(it.url), thumbnail_url: normalizeMediaUrl(it.thumbnail_url) })));
    }catch(e){ console.error(e); }
  },[id, isNew]);

  React.useEffect(()=>{ loadRegions(); }, [loadRegions]);
  React.useEffect(()=>{ loadDistrict(); }, [loadDistrict]);
  React.useEffect(()=>{ loadImages(); }, [loadImages]);

  const save = async ()=>{
    setBusy(true); setErr(''); setSaved(false);
    try{
      const payload = {
        name_ru: form.name_ru || undefined,
        name_en: form.name_en || undefined,
        name_zh: form.name_zh || undefined,
        slug: isNew ? undefined : (form.slug || undefined),
        description_ru: form.description_ru || undefined,
        description_en: form.description_en || undefined,
        description_zh: form.description_zh || undefined,
        region_id: form.region_id ? Number(form.region_id) : undefined,
        is_active: !!form.is_active,
        sort_order: Number(form.sort_order) || 0,
        lat: form.lat!=='' ? Number(form.lat) : undefined,
        lng: form.lng!=='' ? Number(form.lng) : undefined,
        address: form.address || undefined,
      };
      if (isNew) {
        const r = await AdminAPI.createDistrict(payload);
        setSaved(true);
        const newId = r?.district?.id;
        if (newId) nav(`/admin/districts/${newId}`, { replace: true, state: { district: r.district } });
      } else {
        await AdminAPI.updateDistrict(id, payload);
        setSaved(true);
      }
    }catch(e){ setErr(e?.payload?.details || e?.message || 'Ошибка сохранения'); }
    finally{ setBusy(false); }
  };

  const attachMedia = async (files)=>{
    if (!files?.length || isNew) return;
    for (const f of files){
      await AdminAPI.attachDistrictMedia(id, { media_id: f.id, is_active: true });
    }
    await loadImages();
  };

  const updateLink = async (linkId, patch)=>{
    await AdminAPI.updateDistrictMedia(id, linkId, patch);
    await loadImages();
  };

  const deleteLink = async (linkId)=>{
    if(!window.confirm('Удалить изображение из галереи?')) return;
    await AdminAPI.deleteDistrictMedia(id, linkId);
    await loadImages();
  };

  return (
    <Wrap>
      <Card>
        <h3>{isNew ? 'Создать район' : `Редактировать район #${id}`}</h3>
        {err && <div style={{color:'#e66', marginBottom:8}}>{String(err)}</div>}
        {saved && <div style={{color:'#2e7d32', marginBottom:8}}>Сохранено</div>}

        <Grid>
          <div>
            <Row3>
              <Field>
                <small>Регион</small>
                <select value={form.region_id} onChange={e=>patch({ region_id: e.target.value })}>
                  <option value="">— выберите —</option>
                  {regions.map(r=> <option key={r.id} value={r.id}>{r.name_ru || r.name_en} ({r.code})</option>)}
                </select>
              </Field>
              <Field>
                Активен
                <YesNoToggle value={form.is_active} onChange={(value) => patch({ is_active: value })} />
              </Field>
              <Field>
                <small>Сортировка</small>
                <input type="number" value={form.sort_order} onChange={e=>patch({ sort_order: e.target.value })} />
              </Field>
            </Row3>

            <Row3>
              <Field>
                <small>Название RU</small>
                <input value={form.name_ru} onChange={e=>patch({ name_ru: e.target.value })} />
              </Field>
              <Field>
                <small>Название EN</small>
                <input value={form.name_en} onChange={e=>patch({ name_en: e.target.value })} />
              </Field>
              <Field>
                <small>Название ZH</small>
                <input value={form.name_zh} onChange={e=>patch({ name_zh: e.target.value })} placeholder="例如：莫斯科河区" />
              </Field>
            </Row3>

            {!isNew && (
              <Field>
                <small>Slug</small>
                <input value={form.slug} onChange={e=>patch({ slug: e.target.value })} />
              </Field>
            )}

            <Row3>
              <Field>
                <small>Описание RU</small>
                <textarea rows={6} value={form.description_ru} onChange={e=>patch({ description_ru: e.target.value })} />
              </Field>
              <Field>
                <small>Описание EN</small>
                <textarea rows={6} value={form.description_en} onChange={e=>patch({ description_en: e.target.value })} />
              </Field>
              <Field>
                <small>Описание ZH</small>
                <textarea rows={6} value={form.description_zh} onChange={e=>patch({ description_zh: e.target.value })} placeholder="历史悠久的地区..." />
              </Field>
            </Row3>

            <Row3>
              <Field>
                <small>Адрес</small>
                <input value={form.address} onChange={e=>patch({ address: e.target.value })} />
              </Field>
              <Field>
                <small>Lat</small>
                <input type="number" step="0.000001" value={form.lat} onChange={e=>patch({ lat: e.target.value })} />
              </Field>
              <Field>
                <small>Lng</small>
                <input type="number" step="0.000001" value={form.lng} onChange={e=>patch({ lng: e.target.value })} />
              </Field>
            </Row3>
          </div>

          {!isNew && (
            <div>
              <h3>Галерея</h3>
              <Actions>
                <Btn onClick={()=>setPickerOpen(true)}>+ Добавить фото</Btn>
                <Badge>Всего: <b>{images.length}</b></Badge>
              </Actions>
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10, marginTop:10}}>
                {images.map(img=> (
                  <div key={img.id} style={{border:'1px solid var(--bdr)', borderRadius:12, overflow:'hidden'}}>
                    <img src={normalizeMediaUrl(img.thumbnail_url || img.url)} alt="" style={{width:'100%',height:120,objectFit:'cover'}} />
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, padding:6}}>
                      <label style={{fontSize:12}}>Активен
                        <select value={img.is_active? '1':'0'} onChange={e=>updateLink(img.id, { is_active: e.target.value==='1' })}>
                          <option value="1">Да</option><option value="0">Нет</option>
                        </select>
                      </label>
                      <label style={{fontSize:12}}>Главное
                        <select value={img.is_main? '1':'0'} onChange={e=>updateLink(img.id, { is_main: e.target.value==='1' })}>
                          <option value="1">Да</option><option value="0">Нет</option>
                        </select>
                      </label>
                      <label style={{fontSize:12}}>Порядок
                        <input type="number" defaultValue={img.sort_order||0} onBlur={e=>{ const v=Number(e.target.value)||0; if(v!== (img.sort_order||0)) updateLink(img.id, { sort_order: v }); }} />
                      </label>
                    </div>
                    <div style={{display:'flex', gap:6, padding:6}}>
                      <Btn onClick={()=>deleteLink(img.id)}>Удалить</Btn>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Grid>

        <Actions style={{marginTop:10}}>
          <Btn onClick={()=>nav('/admin/districts')}>← К списку</Btn>
          <div style={{flex:1}} />
          <Btn $kind="primary" onClick={save} disabled={busy}>{busy ? 'Сохранение…' : (isNew ? 'Создать' : 'Сохранить')}</Btn>
        </Actions>
      </Card>

      <MediaPickerModal open={pickerOpen} onClose={()=>setPickerOpen(false)} onPick={attachMedia} multi />
    </Wrap>
  );
}


